import { useEffect, useState } from 'react';

import { analytics } from '@sureapp/wl-toggle-common/dist/utils';
import { scrollIntoView } from '@sureapp/wl-toggle-common/dist/utils/dom';
import clsx from 'clsx';
import get from 'lodash/get';
import map from 'lodash/map';
import qs from 'qs';
import { Helmet } from 'react-helmet';
import LazyLoad from 'react-lazyload';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';

// Hero images
import Footer from '@@/common/components/Footer';
import Modal from '@@/common/components/Modal';
import NavBar from '@@/common/components/NavBar';
import { useTimeout } from '@@/common/hooks/useTimeout';

import imgHeroDog from './assets/hero-dog@2x.png';
import imgHeroDogWebp from './assets/hero-dog@2x.webp';
import imgHeroIdea from './assets/hero-idea.png';
import imgHeroIdeaWebp from './assets/hero-idea.webp';
import imgHeroIdea2x from './assets/hero-idea@2x.png';
import imgHeroIdeaWebp2x from './assets/hero-idea@2x.webp';
import imgHeroMemories from './assets/hero-memories@2x.png';
import imgHeroMemoriesWebp from './assets/hero-memories@2x.webp';
// Section images
import iconBrunch from './assets/icon-brunch.svg';
import iconHeadphones from './assets/icon-headphones.svg';
import iconLatte from './assets/icon-latte.svg';
import imgCoffee from './assets/landing-coffee.png';
import imgCoffeeWebp from './assets/landing-coffee.webp';
import imgCreditLift from './assets/landing-credit-lift.png';
import imgCreditLiftWebp from './assets/landing-credit-lift.webp';
import imgDog from './assets/landing-dog.png';
import imgDogWebp from './assets/landing-dog.webp';
import imgHustle from './assets/landing-hustle.png';
import imgHustleWebp from './assets/landing-hustle.webp';
import imgIdentity from './assets/landing-identity.png';
import imgIdentityWebp from './assets/landing-identity.webp';
import imgItems from './assets/landing-items.png';
import imgItemsWebp from './assets/landing-items.webp';
import imgTravel from './assets/landing-travel.png';
import imgTravelWebp from './assets/landing-travel.webp';
import imgSubscriptionsRound from './assets/subscriptions-round.png';

import './LandingHome.css';

function LandingHome() {
  const { setTimeoutSafe } = useTimeout();
  const [windowWidth, setWindowWidth] = useState(0);
  const [isHeroImgVisible, setIsHeroImgVisible] = useState(true);
  const [heroLandingId, setHeroLandingId] = useState(0);
  const [isSelfReferralModalOpen, setIsSelfReferralModalOpen] = useState(false);
  const [initHeroIntervalId, setInitHeroIntervalId] = useState(0);

  function updateWidth() {
    const updatedWindowWidth = Math.max(
      document.documentElement.clientWidth,
      window.innerWidth || 0
    );
    setWindowWidth(updatedWindowWidth);
  }

  const showSelfReferralModal = () => {
    setIsSelfReferralModalOpen(true);
  };

  useEffect(() => {
    analytics.track('Lead', {
      currency: 'USD',
    });
    analytics.page();
    updateWidth();
    window.addEventListener('resize', updateWidth);
    if (window.location.hash) {
      const sectionDom = document.querySelector(window.location.hash)!;
      setTimeout(() => {
        scrollIntoView(sectionDom as HTMLElement);
      }, 300);
    }

    const query = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });
    if (get(query, 'self_referral')) {
      showSelfReferralModal();
    }

    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  useEffect(
    function initHeroTransitionInterval() {
      /*
       * Initializes the interval and swaps the hero
       * image after the fadeout animation starts
       */
      if (initHeroIntervalId === 0) {
        setInitHeroIntervalId(
          setTimeoutSafe(() => {
            setInitHeroIntervalId(1);
            setIsHeroImgVisible(false);
            setTimeoutSafe(() => {
              setHeroLandingId((landingId) => (landingId + 1) % 3);
              setIsHeroImgVisible(true);
            }, 700);
          }, 4700)
        );
      }

      return () => {
        if (initHeroIntervalId !== 0) {
          window.clearTimeout(initHeroIntervalId);
          setInitHeroIntervalId(0);
        }
      };
    },
    [heroLandingId, initHeroIntervalId, isHeroImgVisible, setTimeoutSafe]
  );

  const handleTellMeMoreClick = () => {
    analytics.track('Tell Me More Clicked');
  };

  const handleCtaClick = (e: any) => {
    analytics.track('Landing CTA Clicked', {
      value: e.currentTarget.dataset.gaValue,
    });
  };

  function getSubscriptions() {
    return [
      {
        title: 'Basic',
        body:
          'The liability coverage many landlords require + a little coverage for your stuff.',
        priceImg: iconLatte,
        altTag: 'A latte',
        priceText: 'For about the price of a latte per month.',
      },
      {
        title: 'Standard',
        body:
          'A step up from Basic for the person who has a little more stuff they want to cover.',
        priceImg: iconHeadphones,
        altTag: 'A figure wearing headphones',
        priceText: 'For around the price of a streaming service per month.',
      },
      {
        title: 'Premium',
        body:
          'Next level coverage for folks living large with nicer items they want to protect.',
        priceImg: iconBrunch,
        altTag: 'A stack of pancakes',
        priceText: 'For roughly the price of Sunday brunch per month.',
      },
    ];
  }

  function getSections() {
    return [
      {
        key: 'travel',
        hash: 'travel',
        heading: 'Protection on \nthe road. Or rue.',
        body:
          'At Toggle, we think coverage should travel with you. That means if you lose your laptop in Paris or your phone in Portland, you’re covered. Anytime. Anywhere.',
        toggleText: 'Coverage.',
        img: imgTravel,
        imgWebp: imgTravelWebp,
        imgAlt: 'The Eiffel Tower overlooking the Paris skyline',
      },
      {
        key: 'pet',
        hash: 'pet-parent',
        heading: 'Love the ones\nthat love you\nunconditionally.',
        body:
          "Whether you have a furry baby or a scaly one, make sure you and your pet are protected when things go awry—just Toggle On℠ Pet Parent℠. From dog bites to carpet damage, we've got you covered during your lil buddy's not so lovable moments.",
        toggleText: 'Pet Parent',
        img: imgDog,
        imgWebp: imgDogWebp,
        imgAlt: 'A pug',
      },
      {
        key: 'rewards',
        hash: 'rewards',
        heading: 'We think loyalty and good behavior should be rewarded.',
        body:
          "That's why you'll see your deductible decline each claim free year you remain part of our Toggle® family.",
        toggleText: 'Rewards are always',
        img: imgCoffee,
        imgWebp: imgCoffeeWebp,
        imgAlt: 'A coffee',
      },
      {
        key: 'itemCoverage',
        hash: 'coverage',
        heading: 'Camera?\nComputer?\nCovered.',
        body:
          "Not all renters insurance policies protect your more expensive items. Ours can. Your camera, phone, computer and more are covered up to the amount you Toggle On℠. It's that easy.",
        toggleText: 'High value items',
        img: imgItems,
        imgWebp: imgItemsWebp,
        imgAlt: 'An iPhone taking a picture of an array of expensive items',
      },
      {
        key: 'sideHustle',
        hash: 'side-hustle',
        heading: 'Got a weekend gig? Building your passion project?',
        body:
          "If you've got a thing or many things on the side, like food blogging or jewelry making, Toggle On℠ Side Hustle® coverage to protect your side income and equipment. Sorry, ride share drivers - we're not covering that just yet.",
        toggleText: 'Side Hustle',
        img: imgHustle,
        imgWebp: imgHustleWebp,
        imgAlt: 'A woman working at a desk',
      },
      {
        key: 'identity',
        hash: 'identity-protection',
        heading: 'Your identity should only belong to you.',
        body:
          'Hackers happen. Protect yourself with monitoring alerts and professional support when it matters most. Toggle On℠ Identity Protector to start your coverage.',
        toggleText: 'Identity Protector',
        img: imgIdentity,
        imgWebp: imgIdentityWebp,
        imgAlt: 'A man on a phone holding a coffee',
      },
      {
        key: 'credit',
        hash: 'credit-lift',
        heading: 'Get credit for paying your rent.',
        body: (
          <span>
            With Credit Lift
            <sup style={{ fontWeight: 100 }}>&#x2120;</sup> by LevelCredit
            <sup>&trade;</sup>, we’ll help you get the credit you deserve for
            those on-time rent payments.
          </span>
        ),
        bodyAddition: (
          <>
            <br />
            <br />
            <a
              href="https://www.levelcredit.com/partners/credit-lift/"
              className="LandingHome-credit-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more about Credit Lift
            </a>{' '}
            to start building your credit for a brighter financial future.
          </>
        ),
        toggleText: 'Credit Lift',
        img: imgCreditLift,
        imgWebp: imgCreditLiftWebp,
        imgAlt: 'A computer desk',
      },
    ];
  }

  function getCoveredItemText() {
    if (heroLandingId === 1 && windowWidth >= 768) {
      return 'Henry';
    }
    if (heroLandingId === 2 && windowWidth >= 768) {
      return 'mementos';
    }
    return 'tech';
  }

  function getHeroImgNode() {
    if (windowWidth >= 768) {
      return (
        <>
          <picture>
            <source srcSet={imgHeroIdeaWebp} type="image/webp" />
            <source
              srcSet={imgHeroIdeaWebp2x}
              type="image/webp"
              media="(min-width: 1600px)"
            />
            <source srcSet={imgHeroIdea} type="image/png" />
            <source
              srcSet={imgHeroIdea2x}
              type="image/png"
              media="(min-width: 1600px)"
            />
            <img
              src={imgHeroIdea}
              alt="Woman on laptop"
              className={clsx('LandingHome-hero-image1', {
                'LandingHome-visible': isHeroImgVisible && heroLandingId === 0,
              })}
            />
          </picture>
          <picture>
            <source srcSet={imgHeroDogWebp} type="image/webp" />
            <source srcSet={imgHeroDog} type="image/png" />
            <img
              src={imgHeroDog}
              alt="Man carrying a dog in a backpack"
              className={clsx('LandingHome-hero-image2', {
                'LandingHome-visible': isHeroImgVisible && heroLandingId === 1,
              })}
            />
          </picture>
          <picture>
            <source srcSet={imgHeroMemoriesWebp} type="image/webp" />
            <img
              src={imgHeroMemories}
              alt="Woman putting up pictureframe"
              className={clsx('LandingHome-hero-image3', {
                'LandingHome-visible': isHeroImgVisible && heroLandingId === 2,
              })}
            />
          </picture>
        </>
      );
    }
    return (
      <picture>
        <source srcSet={imgHeroIdeaWebp} type="image/webp" />
        <source srcSet={imgHeroIdea} type="image/png" />
        <img
          src={imgHeroIdea}
          alt="Woman on laptop"
          className="LandingHome-hero-image1"
        />
      </picture>
    );
  }

  const toggleNode = (copy: string) => (
    <div className="LandingHome-toggleWrapper">
      <span className="LandingHome-toggleCopy">{copy}</span>
      <div className="LandingHome-toggleSwitchContainer">
        <span className="LandingHome-toggleLabel">On</span>
        <div className="LandingHome-toggleSwitch">&nbsp;</div>
      </div>
    </div>
  );

  const hideSelfReferralModal = () => {
    setIsSelfReferralModalOpen(false);
  };

  return (
    <div className="LandingHome" data-sentry-unmask>
      <Helmet>
        <title>Toggle Renters Insurance | Covered. Anytime. Anywhere.</title>
        <meta
          name="description"
          content="You, and your things, are covered wherever you live, travel, and visit. Sign up once, and your coverage follows you wherever you go, until you cancel it."
        />
      </Helmet>
      <NavBar
        overrideClasses={{
          root: 'LandingHome-NavbarTemp',
          navbar_temp: 'LandingHome-NavbarTemp',
        }}
      />
      <header className="LandingHome-hero">
        <div className="LandingHome-heroContainer">
          <h3 className="LandingHome-hero-greeting">
            Renters insurance you can customize.
          </h3>
          <h1 className="LandingHome-hero-heading">
            You and your
            <span className="LandingHome-hero-heading-item">
              <em
                className={clsx('LandingHome-hero-heading-item-text', {
                  'LandingHome-visible': isHeroImgVisible,
                })}
              >
                {getCoveredItemText()}
              </em>
            </span>
            <strong className="LandingHome-hero-heading-covered">
              covered.
            </strong>
          </h1>
          <p className="LandingHome-hero-tagline">
            <span className="LandingHome-hero-tagline-word">Anytime.</span>
            <span className="LandingHome-hero-tagline-word">Anywhere.</span>
          </p>
          {getHeroImgNode()}
          <Link
            className="LandingHome-hero-cta btn btn-secondary btn-assertive"
            data-testid="LandingHome-hero-cta"
            onClick={handleTellMeMoreClick}
            to="/sign-up"
          >
            Get my price
          </Link>
        </div>
      </header>
      <main>
        <section className="LandingHome-subscriptions">
          <h2 className="LandingHome-subscriptions-heading">
            Toggle® offers renters insurance subscriptions as unique as you
          </h2>
          <div className="LandingHome-subscriptions-sliderWrapper">
            <Slider
              adaptiveHeight={false}
              slidesToShow={3}
              infinite={false}
              arrows={false}
              dots
              dotsClass="slick-dots"
              centerPadding="80px"
              centerMode
              responsive={[
                {
                  breakpoint: 768,
                  settings: {
                    centerMode: true,
                    centerPadding: '20px',
                    slidesToShow: 1,
                  },
                },
              ]}
            >
              {map(getSubscriptions(), (subscription) => (
                <div
                  className="LandingHome-subscriptions-cardWrapper"
                  key={subscription.title}
                >
                  <div
                    className={`LandingHome-subscriptions-card LandingHome-subscriptions-card--${subscription.title.toLowerCase()}`}
                  >
                    <h5 className="LandingHome-subscriptions-title">
                      {subscription.title}
                    </h5>
                    <div
                      className="LandingHome-subscriptions-body"
                      data-key={subscription.title}
                    >
                      {subscription.body}
                    </div>
                    <div className="LandingHome-subscriptions-imageWrapper">
                      <span className="LandingHome-subscriptions-dollarSign">
                        $
                      </span>
                      <img
                        className="LandingHome-subscriptions-image"
                        src={subscription.priceImg}
                        alt={subscription.altTag}
                      />
                      <span className="LandingHome-subscriptions-paymentFrequency">
                        /mo
                      </span>
                    </div>
                    <h3 className="LandingHome-subscriptions-priceText">
                      {subscription.priceText}
                    </h3>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
          <Link
            className="LandingHome-subscriptions-cta btn btn-secondary btn-success"
            data-testid="LandingHome-subscriptions-cta"
            to="/sign-up"
            data-ga-value="0"
            onClick={handleCtaClick}
          >
            Price it for me
          </Link>
          {windowWidth >= 768 && (
            <img
              className="LandingHome-subscriptions-backgroundImage"
              src={imgSubscriptionsRound}
              alt="A table with items on it"
            />
          )}
        </section>
        <section className="LandingHome-coverage">
          <div className="LandingHome-coverage-header">
            <h2 className="LandingHome-coverage-signaturePrompt">
              Toggle renters insurance.
              <br />
              We cover you so you can
            </h2>
            <h2 className="LandingHome-coverage-signature">
              get on with your life
            </h2>
            <hr className="LandingHome-coverage-signatureUnderline" />
          </div>
        </section>
        {getSections().map((section) => (
          <section
            className={`LandingHome-${section.key}`}
            key={section.key}
            id={section.hash}
          >
            <div className="LandingHome-contentWrapper">
              <LazyLoad height={200} once>
                <picture className={`LandingHome-${section.key}-imageWrapper`}>
                  <source srcSet={section.imgWebp} type="image/webp" />
                  <source srcSet={section.img} type="image/png" />
                  <img
                    className={`LandingHome-${section.key}-image`}
                    src={section.img}
                    alt={section.imgAlt}
                  />
                </picture>
              </LazyLoad>
              <div className="LandingHome-copyWrapper">
                <div className="LandingHome-bodyWrapper">
                  <h2 className="LandingHome-heading">{section.heading}</h2>
                  <p className="LandingHome-body">
                    {section.body}
                    {section.bodyAddition || null}
                  </p>
                </div>
                {toggleNode(section.toggleText)}
              </div>
            </div>
          </section>
        ))}
        <section className="LandingHome-signUp">
          <h2 className="LandingHome-signUp-heading">
            All right, I&apos;m ready to go!
          </h2>
          <Link
            className="LandingHome-signUp-cta btn btn-secondary btn-success"
            data-testid="LandingHome-signUp-cta"
            to="/sign-up"
            data-ga-value="1"
            onClick={handleCtaClick}
          >
            Sign me up
          </Link>
        </section>
      </main>
      <Footer />
      <Modal
        isOpen={isSelfReferralModalOpen}
        onRequestClose={hideSelfReferralModal}
        title=""
      >
        <div className="Modal-body" data-testid="selfReferralModal">
          <p>
            We’ve detected that you have clicked your own referral link. Please
            note that any purchases made will not qualify for a reward. Refer
            your friends to earn rewards!
          </p>
        </div>
        <div className="Modal-footer">
          <button
            type="button"
            className="btn btn-secondary btn-assertive"
            onClick={hideSelfReferralModal}
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default LandingHome;
